import { useState } from "react"
import { toast } from "sonner"

import { ApiError } from "@/api/client"
import { useBorrarCredencial, useCredenciales, useGuardarCredencial } from "@/api/queries"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatearFecha } from "@/shared/format"

/**
 * Credenciales BYOK: las claves de proveedor que trae la organización.
 *
 * **La clave no vuelve nunca.** El gateway solo cuenta que existe, con qué versión de
 * su clave maestra está cifrada y cuándo se rotó — ni siquiera enmascarada. Esta
 * pantalla no intenta compensarlo: no hay "mostrar", ni "copiar", ni un `sk-…3f9a` que
 * dé la sensación de estar viendo algo. Si hay que comprobar que es la correcta, se
 * rota.
 *
 * Solo la ve un `owner`. Para cualquier otro rol, el gateway devuelve 403 y aquí se
 * pinta ese 403 tal cual: la decisión es suya, no de la consola.
 */
const PROVEEDORES = [
  { id: "deepseek", nombre: "DeepSeek" },
  { id: "gemini", nombre: "Gemini" },
]

export function CredentialsPage() {
  const credenciales = useCredenciales()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Credenciales</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tu consumo se factura en tu propia cuenta de cada proveedor. El gateway no
          revende tokens.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {credenciales.isPending ? (
            <Skeleton className="h-32" />
          ) : credenciales.isError ? (
            <p className="text-sm text-destructive">{mensajeDe(credenciales.error)}</p>
          ) : credenciales.data.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Todavía no has subido ninguna credencial. Hasta que lo hagas, tus
              aplicaciones no pueden despachar.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Subida</TableHead>
                  <TableHead>Última rotación</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {credenciales.data.map((c) => (
                  <TableRow key={c.provider_id}>
                    <TableCell className="font-medium">{nombreDe(c.provider_id)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatearFecha(c.created_at)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.rotated_at === null ? "nunca" : formatearFecha(c.rotated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Borrar providerId={c.provider_id} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Subir />
    </div>
  )
}

function nombreDe(id: string): string {
  return PROVEEDORES.find((p) => p.id === id)?.nombre ?? id
}

function mensajeDe(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return "Solo el propietario de la organización puede ver las credenciales."
  }
  return "No se pudieron cargar las credenciales."
}

function Borrar({ providerId }: { providerId: string }) {
  const borrar = useBorrarCredencial()

  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={borrar.isPending}
      onClick={() => {
        borrar.mutate(providerId, {
          onSuccess: () => toast.success(`Credencial de ${nombreDe(providerId)} eliminada.`),
          onError: () => toast.error("No se pudo eliminar."),
        })
      }}
    >
      Eliminar
    </Button>
  )
}

function Subir() {
  const [providerId, setProviderId] = useState(PROVEEDORES[0]?.id ?? "")
  const [apiKey, setApiKey] = useState("")
  const guardar = useGuardarCredencial()

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    try {
      await guardar.mutateAsync({ providerId, apiKey: apiKey.trim() })
      // Se limpia el campo en cuanto sale: la clave no tiene por qué seguir en el DOM
      // ni un segundo más, y volver a verla ahí invita a copiarla a otro sitio.
      setApiKey("")
      toast.success("Guardada. Se usará en la siguiente petición.")
    } catch (e) {
      toast.error(
        e instanceof ApiError && e.status === 403
          ? "Solo el propietario puede subir credenciales."
          : "No se pudo guardar la credencial.",
      )
    }
  }

  return (
    <Card className="max-w-md">
      <CardHeader>
        <CardTitle>Subir o rotar</CardTitle>
        <CardDescription>
          Subir una clave donde ya había otra la sustituye. Surte efecto en la siguiente
          petición.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={enviar} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="proveedor">Proveedor</Label>
            <select
              id="proveedor"
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
            >
              {PROVEEDORES.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="api-key">Clave</Label>
            <Input
              id="api-key"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-…"
              required
            />
            <p className="text-xs text-muted-foreground">
              Se guarda cifrada y no se puede volver a consultar, ni desde aquí ni desde
              ningún sitio.
            </p>
          </div>
          <Button type="submit" disabled={guardar.isPending || apiKey.trim().length < 8}>
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
